import React, { useState } from "react";
import {
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button
} from "@material-ui/core";
import { Delete, FileCopy } from "@material-ui/icons";
import { useGetCustomerContractPriceListsQuery } from "@graphql";
import { useLocale, useNavManager, useUserProfile } from "@hooks";
import { CustomerContractPriceListEditorLocation } from "@contracts/locations/CustomerContractPriceListEditorLocation";
import { Body, ErrorCard, Spinner } from "@components";
import { formatDate } from "../utils";
import { useRemovePriceList } from "./useRemovePriceList";

const MAX_CUSTOMER_COUNT = 3;

type Props = {
  id: string;
};

export function CustomerContractPriceLists(props: Props) {
  const { data, loading, error } = useGetCustomerContractPriceListsQuery({
    variables: { id: props.id },
    fetchPolicy: "cache-first"
  });
  const { remove, removingIds } = useRemovePriceList();
  const { can } = useUserProfile();

  const { locale } = useLocale();
  const { navManager } = useNavManager();
  const [show, setShow] = useState(false);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorCard message={error.message} title={error.name} />;
  }

  const { customerContractById: contract } = data;
  const isEmpty = contract.priceLists.length === 0;

  const onNew = () => {
    navManager.open(
      `customer_contract_new_price_list`,
      new CustomerContractPriceListEditorLocation(props.id, null, false)
    );
  };

  const onOpen = (id: string) => {
    navManager.open(
      `customer_contract_price_list_${id}`,
      new CustomerContractPriceListEditorLocation(props.id, id, false)
    );
  };

  const duplicate = (id: string) => {
    navManager.open(
      `customer_contract_price_list_${id}`,
      new CustomerContractPriceListEditorLocation(props.id, id, true)
    );
  };

  return (
    <Paper>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{locale.customerContractPriceLists.name}</TableCell>
            <TableCell>{locale.customerContractPriceLists.startDate}</TableCell>
            <TableCell>{locale.customerContractPriceLists.endDate}</TableCell>
            <TableCell style={{ maxWidth: "50%" }}>
              {locale.customerContractPriceLists.customers}
            </TableCell>
            <TableCell colSpan={2} />
          </TableRow>
        </TableHead>

        {isEmpty && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={6} align="center">
                {locale.customerContractPriceLists.empty}
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        <TableBody>
          {contract.priceLists.map((priceList) => {
            const customerNames = priceList.customers.map((x) => x.Name);
            const listCustomers = !show
              ? customerNames.slice(0, MAX_CUSTOMER_COUNT).join(", ")
              : customerNames.join(", ");
            const extra = priceList.customerCount - MAX_CUSTOMER_COUNT;

            return (
              <TableRow
                onClick={() => onOpen(priceList._id)}
                key={priceList._id}
                hover
                style={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Body>{priceList.name}</Body>
                </TableCell>
                <TableCell style={styles.dateCol}>
                  <Body>{formatDate(priceList.startDate)}</Body>
                </TableCell>
                <TableCell style={styles.dateCol}>
                  <Body>{formatDate(priceList.endDate)}</Body>
                </TableCell>
                <TableCell>
                  <Body>{listCustomers}</Body>
                  {extra > 0 && !show && <Body>&nbsp;+{extra}</Body>}
                  {extra > 0 && (
                    <Body
                      style={{ cursor: "pointer" }}
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShow(!show);
                      }}
                    >
                      &nbsp;{show ? locale.SeeLess : locale.SeeMore}
                    </Body>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicate(priceList._id);
                    }}
                    disabled={
                      !can("customer-contract:edit:all") ||
                      removingIds.includes(priceList._id)
                    }
                  >
                    <FileCopy />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(priceList._id);
                    }}
                    disabled={
                      !can("customer-contract:edit:all") ||
                      removingIds.includes(priceList._id)
                    }
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}

          <TableRow>
            <TableCell colSpan={5} align="center" style={{ borderBottom: 0 }}>
              <Button
                variant="outlined"
                onClick={onNew}
                disabled={!can("customer-contract:edit:all")}
              >
                {locale.customerContractPriceLists.nothingFoundAddNew}
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
}

const styles = {
  dateCol: { whiteSpace: "nowrap" }
} as Record<string, React.CSSProperties>;
